import React, { useEffect, useRef, useState, useCallback } from 'react'
import Keyboard from 'simple-keyboard'
import 'simple-keyboard/build/css/index.css'
import Button from '@renderer/components/Button'
import { t } from 'i18next'
import { Html5Qrcode, Html5QrcodeResult } from 'html5-qrcode'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import Preview from '@renderer/components/Preview'
import cogoToast from '@successtar/cogo-toast'
import { useDesignLayout, useSessionLayout } from '@renderer/hooks'
import { useConfigStore } from '@renderer/stores/configState'
import { IoQrCode, IoKeypad } from 'react-icons/io5'

interface OnScreenKeyboardProps {
  onEnter: (value: string) => void
  onClose: () => void
  placeholder?: string
  newspaperMode?: boolean
  error?: string
  isLoading?: boolean
  onInputChange?: () => void
  /** `email`: normal case, no QR tab, full keyboard for addresses */
  variant?: 'voucher' | 'email'
  /** Replaces voucher title (e.g. email gallery) */
  modalTitle?: string
  /** Primary action label (default: Apply) */
  submitLabel?: string
  /** Label while loading (default: Applying...) */
  submitLoadingLabel?: string
  /** Extra classes on the fullscreen overlay (e.g. z-index above other modals) */
  overlayClassName?: string
}

type InputMode = 'keyboard' | 'qr'

/** Voucher codes are uppercase only — normalize every entry path. */
function normalizeVoucherInput(value: string): string {
  return value.toUpperCase()
}

/** Small gap above the screen bottom so the fixed “Powered by” line (View) isn’t tight against keys */
const KB_WATERMARK_RESERVE_CLASS = 'pb-5'

// Removed preprocessing function - using raw images for better QR detection

const OnScreenKeyboard: React.FC<OnScreenKeyboardProps> = ({
  onEnter,
  onClose,
  placeholder = t('checkout.voucherCodePlaceholder', 'Type or paste your code'),
  newspaperMode = false,
  error,
  isLoading = false,
  onInputChange,
  variant = 'voucher',
  modalTitle,
  submitLabel,
  submitLoadingLabel,
  overlayClassName = ''
}) => {
  const isEmailVariant = variant === 'email'
  const normalizeInput = useCallback(
    (value: string): string => (isEmailVariant ? value : normalizeVoucherInput(value)),
    [isEmailVariant]
  )
  const keyboardRef = useRef<Keyboard | null>(null)
  const qrScannerRef = useRef<Html5Qrcode | null>(null)
  const qrElementRef = useRef<HTMLDivElement>(null)
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const onInputChangeRef = useRef(onInputChange)
  onInputChangeRef.current = onInputChange
  const onEnterRef = useRef(onEnter)
  onEnterRef.current = onEnter

  const [inputMode, setInputMode] = useState<InputMode>('keyboard')
  const [isScanning, setIsScanning] = useState(false)
  const [scanError, setScanError] = useState<string>('')
  const [internalInputValue, setInternalInputValue] = useState('')
  const [scanAttempts, setScanAttempts] = useState(0)
  const [lastDetectedCode, setLastDetectedCode] = useState<string>('')

  const cameraKey =
    (useSessionStateStore().userPreferences.selectedCamera as 'primary' | 'secondary') || 'primary'
  const { config: boothConfig } = useConfigStore()
  const { primaryColor, secondaryColor } = boothConfig?.theme || {
    primaryColor: '#3B82F6',
    secondaryColor: '#FFFFFF'
  }
  const { isCompact: kbCompact, isLandscape: kbLandscape, designH: kbDesignH } = useDesignLayout()
  const { isCompact: viewportCompact, isLandscapeViewport } = useSessionLayout()
  /** Real phone/tablet in landscape: short vertical space — use dense keyboard + split layout */
  const kbViewportShortLandscape = viewportCompact && isLandscapeViewport
  /** Design or device landscape — segmented control must stay equal-width (no sm:flex-none) */
  const kbAnyLandscape = kbLandscape || isLandscapeViewport
  const kbPortraitCompact = kbCompact && !kbLandscape
  /** Spacious landscape kiosk — smaller modal chrome; not phone short-landscape / portrait-compact */
  const kbRegularLandscape =
    kbLandscape && !kbPortraitCompact && !kbViewportShortLandscape
  /** Mobile key rows (wide tap targets per row) for design portrait or physical landscape phone */
  const useMobileKeyLayout = kbPortraitCompact || kbViewportShortLandscape
  const [viewportH, setViewportH] = useState(
    () => (typeof window !== 'undefined' ? window.innerHeight : 640)
  )
  useEffect(() => {
    const upd = (): void => setViewportH(window.innerHeight)
    window.addEventListener('resize', upd)
    window.addEventListener('orientationchange', upd)
    return (): void => {
      window.removeEventListener('resize', upd)
      window.removeEventListener('orientationchange', upd)
    }
  }, [])
  const kbMaxPx = kbPortraitCompact ? Math.round(kbDesignH * 0.38) : undefined
  const kbKeyH = kbPortraitCompact ? Math.max(20, Math.min(36, Math.round(kbDesignH * 0.035))) : undefined
  const kbViewportKeyH = Math.max(22, Math.min(32, Math.round(viewportH * 0.056)))
  const effKeyH = kbViewportShortLandscape ? kbViewportKeyH : kbKeyH
  const keyHForCss = Math.round(effKeyH ?? 28)
  const kbAreaMaxStyle: React.CSSProperties | undefined = kbViewportShortLandscape
    ? { maxHeight: `min(46%, ${Math.round(kbDesignH * 0.46)}px)` }
    : kbMaxPx
      ? { maxHeight: `${kbMaxPx}px` }
      : undefined

  /** Fewer keys per row + flex CSS so keys stay wide enough to tap on narrow portrait screens */
  const mobilePortraitLayout = {
    default: [
      '1 2 3 4 5 6 7 8 9 0',
      'q w e r t y u i o p',
      'a s d f g h j k l',
      '{shift} z x c v b n m {bksp}',
      '@ - _ . {space}'
    ],
    shift: [
      '1 2 3 4 5 6 7 8 9 0',
      'Q W E R T Y U I O P',
      'A S D F G H J K L',
      '{shift} Z X C V B N M {bksp}',
      '@ - _ . {space}'
    ]
  }

  /** Desktop: digit row on both layers (shift no longer symbol-only on row 1); letters + email row */
  const desktopLayout = {
    default: [
      '1 2 3 4 5 6 7 8 9 0',
      'q w e r t y u i o p - _ .',
      'a s d f g h j k l {enter}',
      '{shift} z x c v b n m {bksp}',
      '@ .com {space}'
    ],
    shift: [
      '1 2 3 4 5 6 7 8 9 0',
      'Q W E R T Y U I O P - _ .',
      'A S D F G H J K L {enter}',
      '{shift} Z X C V B N M {bksp}',
      '@ .com {space}'
    ]
  }

  // Initialize keyboard when mode or portrait layout changes
  useEffect(() => {
    if (inputMode === 'keyboard') {
      const initialInput = normalizeInput(internalInputValue)
      keyboardRef.current = new Keyboard({
        // Voucher uppercase from normalizeInput. Both desktop layers now start with 0–9; open on default.
        layoutName: 'default',
        onChange: (input: string): void => {
          const next = normalizeInput(input)
          setInternalInputValue(next)
          if (next !== input) {
            keyboardRef.current?.setInput(next)
          }
          onInputChangeRef.current?.()
        },
        onKeyPress: (button: string): void => {
          if (button === '{enter}') {
            const raw = keyboardRef.current?.getInput() ?? ''
            onEnterRef.current(normalizeInput(raw))
          }
        },
        layout: useMobileKeyLayout ? mobilePortraitLayout : desktopLayout,
        theme: 'hg-theme-default hg-layout-default myTheme',
        display: {
          '{bksp}': '⌫',
          '{enter}': '✓',
          '{space}': 'space',
          '{shift}': '⇧',
          '{tab}': '⇥',
          '{lock}': '⇪'
        },
        buttonTheme: useMobileKeyLayout
          ? [{ class: 'hg-button-spaceWide', buttons: '{space}' }]
          : [],
        input: initialInput
      })
    }

    return (): void => {
      keyboardRef.current?.destroy()
    }
  }, [inputMode, useMobileKeyLayout, isEmailVariant, normalizeInput])

  // Keep simple-keyboard string in sync when value changes from the native field or QR scan
  useEffect(() => {
    if (inputMode !== 'keyboard' || !keyboardRef.current) return
    const fromKb = keyboardRef.current.getInput()
    if (fromKb !== internalInputValue) {
      keyboardRef.current.setInput(internalInputValue)
    }
  }, [internalInputValue, inputMode])

  // Stop QR scanning
  const stopQRScanning = useCallback((): void => {
    console.log('[QR Scan] Stopping QR scanning mode...')
    setIsScanning(false)

    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current)
      scanIntervalRef.current = null
      console.log('[QR Scan] Scan interval cleared')
    }
  }, [])

  // QR Code scanning logic using direct camera access (bypassing uniCamUtils)
  const scanQRFromCamera = useCallback(async (): Promise<void> => {
    // Check if we're still in scanning mode
    if (!scanIntervalRef.current) {
      console.log('[QR Scan] Scan attempt skipped - not in scanning mode')
      return
    }

    console.log('[QR Scan] Starting scan attempt...')
    setScanAttempts((prev) => prev + 1)

    try {
      // Use QR-optimized capture method that works with both DSLR and webcam
      console.log('[QR Scan] Using QR-optimized capture method for', cameraKey, 'camera...')

      // Use the new QR-optimized method that respects the user's camera choice
      const blob = await unifiedCameraUtils.capturePreviewForQR(cameraKey)
      console.log(
        '[QR Scan] QR-optimized capture result - blob:',
        blob ? `${blob.size} bytes` : 'null'
      )

      if (!blob) {
        console.error('[QR Scan] Failed to capture image from camera')
        setScanError('Failed to capture image from camera')
        return
      }

      // Skip preprocessing - use raw image
      console.log('[QR Scan] Using RAW image (no preprocessing)...')

      // Convert blob to File for Html5Qrcode
      const file = new File([blob], 'qr-scan.png', { type: blob.type })
      console.log('[QR Scan] Created file for scanning:', file.size, 'bytes, type:', file.type)

      // Save debug image every 3 scans (for debugging)
      if (scanAttempts % 3 === 0 && scanAttempts > 0) {
        const url = URL.createObjectURL(blob)
        console.log('[QR Scan] 🖼️ Debug RAW image #' + scanAttempts + ':', url)
        console.log('[QR Scan] Copy this URL to browser to see what camera captured (RAW)')
      }

      // Create QR scanner instance if not exists
      if (!qrScannerRef.current && qrElementRef.current) {
        console.log('[QR Scan] Initializing Html5Qrcode instance...')
        qrScannerRef.current = new Html5Qrcode('qr-scanner-element')
        console.log('[QR Scan] Html5Qrcode instance created')
      }

      if (qrScannerRef.current) {
        try {
          console.log('[QR Scan] Scanning RAW file for QR code...')
          // Scan the captured image for QR code
          // Try both with and without experimental features
          let result: Html5QrcodeResult | null = null

          try {
            result = await qrScannerRef.current.scanFileV2(file, false) // Standard scan first
            console.log('[QR Scan] ✅ Standard scan successful! Result:', result)
          } catch {
            console.log('[QR Scan] Standard scan failed, trying experimental...')
            result = await qrScannerRef.current.scanFileV2(file, true) // Experimental scan
            console.log('[QR Scan] ✅ Experimental scan successful! Result:', result)
          }

          if (result && result.decodedText) {
            // Check if this is a duplicate detection
            const decodedUpper = normalizeVoucherInput(result.decodedText)
            if (decodedUpper === normalizeVoucherInput(lastDetectedCode)) {
              console.log('[QR Scan] Duplicate QR code detected, ignoring:', result.decodedText)
              return
            }

            // QR code found! Apply the decoded text as voucher code
            console.log('[QR Scan] ✅ QR code detected! Text:', result.decodedText)
            setLastDetectedCode(decodedUpper)
            setInternalInputValue(decodedUpper)
            setScanError('')

            // Stop scanning immediately after successful detection
            stopQRScanning()

            // Show toast notification
            cogoToast.success(`QR Code detected: ${decodedUpper}`)

            // Auto-apply the voucher code
            setTimeout(() => {
              console.log('[QR Scan] Auto-applying voucher code:', decodedUpper)
              onEnterRef.current(decodedUpper)
            }, 500)
          }
        } catch (err) {
          // No QR code found in this frame, continue scanning silently
          // This is expected when no QR code is visible
          console.log('[QR Scan] No QR code found in this frame:', err)

          // Log detailed error every 5th attempt to avoid spam
          if (scanAttempts % 5 === 0) {
            console.log('[QR Scan] Detailed scan error:', err)
          }
        }
      } else {
        console.error('[QR Scan] QR scanner instance not available')
      }
    } catch (error) {
      console.error('[QR Scan] Error during scan:', error)
      setScanError('Error scanning QR code')
    }
  }, [cameraKey, stopQRScanning, scanAttempts])

  // Start QR scanning
  const startQRScanning = useCallback((): void => {
    console.log('[QR Scan] Starting QR scanning mode...')
    setIsScanning(true)
    setScanError('')
    setScanAttempts(0)
    setLastDetectedCode('') // Reset last detected code

    // Scan every 1500ms (1.5 seconds) - optimized for performance
    scanIntervalRef.current = setInterval(() => {
      scanQRFromCamera()
    }, 1500)
    console.log('[QR Scan] Scan interval started (every 1500ms)')
  }, [scanQRFromCamera])

  // Handle mode switching
  const switchToKeyboard = (): void => {
    console.log('[QR Scan] Switching to keyboard mode')
    stopQRScanning()
    setInputMode('keyboard')
  }

  const switchToQR = (): void => {
    console.log('[QR Scan] Switching to QR mode')
    setInputMode('qr')
    startQRScanning()
  }

  // Cleanup on unmount
  useEffect(() => {
    console.log('[QR Scan] OnScreenKeyboard component mounted')
    console.log('[QR Scan] Initial camera key:', cameraKey)
    console.log('[QR Scan] Initial input mode:', inputMode)

    return (): void => {
      console.log('[QR Scan] OnScreenKeyboard component unmounting, cleaning up...')
      stopQRScanning()
      if (qrScannerRef.current) {
        qrScannerRef.current.clear()
        qrScannerRef.current = null
        console.log('[QR Scan] QR scanner instance cleared')
      }
    }
  }, [])

  // Handle close
  const handleClose = (): void => {
    stopQRScanning()
    onClose()
  }

  const containerClass = newspaperMode
    ? 'bg-newspaper-white newspaper-border'
    : 'bg-white shadow-2xl rounded-2xl'
  const titleClass = newspaperMode
    ? 'newspaper-headline text-newspaper-black'
    : kbCompact
      ? 'text-lg font-bold text-carbon'
      : kbRegularLandscape
        ? 'text-3xl font-bold text-carbon sm:text-4xl'
        : 'text-5xl font-bold text-carbon'
  /** Portrait / phone landscape: title stack; compact design landscape: title | toggles */
  const kbHeaderWrap =
    kbPortraitCompact || kbViewportShortLandscape
      ? 'flex w-full min-w-0 flex-col items-stretch gap-2'
      : kbCompact
        ? 'flex w-full min-w-0 flex-row items-center justify-between gap-2'
        : kbRegularLandscape
          ? 'flex w-full flex-col items-center gap-3 sm:gap-4'
          : 'flex w-full flex-col items-center gap-8'
  const kbHeaderTitle =
    kbPortraitCompact || kbViewportShortLandscape
      ? 'w-full text-center text-base font-bold leading-snug'
      : kbCompact
        ? 'min-w-0 flex-1 truncate text-left'
        : ''

  const kbTitle = modalTitle ?? t('checkout.voucherCodeTitle', 'Voucher code')
  const primarySubmitLabel = submitLabel ?? t('checkout.apply', 'Apply')
  const primarySubmitLoadingLabel = submitLoadingLabel ?? t('checkout.applying', 'Applying...')

  return (
    <div
      className={`fixed inset-0 z-[10] flex flex-col overflow-hidden bg-black bg-opacity-90 ${overlayClassName}`.trim()}
    >
      {/* Upper section — capped in keyboard + short-landscape so keys stay on screen */}
      <div
        className={`flex min-h-0 flex-1 overflow-y-auto overflow-x-hidden ${
          inputMode === 'keyboard' && kbViewportShortLandscape
            ? 'items-start justify-center pt-1'
            : inputMode === 'keyboard' && kbLandscape && kbCompact
              ? 'max-h-[50%] items-center justify-center'
              : inputMode === 'keyboard' && kbLandscape
                ? 'max-h-[50%] items-center justify-center'
                : 'items-center justify-center'
        }`}
      >
        <div
          className={`mx-auto flex flex-col items-center ${kbViewportShortLandscape ? 'w-full max-w-[min(20rem,calc(100vw-1rem))] gap-2 px-2 py-2' : kbPortraitCompact ? 'w-full max-w-full gap-2 px-3 pb-2 pt-2' : kbCompact ? 'w-[95%] max-w-full gap-3 p-3' : kbRegularLandscape ? 'w-[90%] max-w-xl gap-5 p-6 sm:max-w-xl' : 'w-[90%] max-w-2xl gap-8 p-12'} ${containerClass}`}
          style={newspaperMode ? { borderRadius: '0', boxShadow: 'none' } : {}}
        >
          <div className={kbHeaderWrap}>
            <h2
              className={
                useMobileKeyLayout
                  ? 'w-full text-center text-lg font-bold leading-snug tracking-tight text-carbon sm:text-xl'
                  : `${titleClass} ${kbHeaderTitle}`
              }
              style={newspaperMode ? {} : {}}
            >
              {kbTitle}
            </h2>

            {/* Mode Toggle — segmented control */}
            {!isEmailVariant && newspaperMode ? (
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={switchToKeyboard}
                  className="newspaper-border shrink-0 font-medium transition-all duration-200 hover:opacity-90"
                  style={{
                    backgroundColor: inputMode === 'keyboard' ? '#1a1a1a' : '#fafaf8',
                    color: inputMode === 'keyboard' ? '#fafaf8' : '#1a1a1a',
                    border: '2px double #2a2a2a',
                    borderRadius: '0',
                    boxShadow: 'none',
                    padding: '12px 24px',
                    fontSize: '18px'
                  }}
                >
                  {t('checkout.keyboard', 'Keyboard')}
                </button>
                <button
                  type="button"
                  onClick={switchToQR}
                  className="newspaper-border shrink-0 font-medium transition-all duration-200 hover:opacity-90"
                  style={{
                    backgroundColor: inputMode === 'qr' ? '#1a1a1a' : '#fafaf8',
                    color: inputMode === 'qr' ? '#fafaf8' : '#1a1a1a',
                    border: '2px double #2a2a2a',
                    borderRadius: '0',
                    boxShadow: 'none',
                    padding: '12px 24px',
                    fontSize: '18px'
                  }}
                >
                  {t('checkout.scanQR', 'Scan QR')}
                </button>
              </div>
            ) : !isEmailVariant ? (
              <div
                className={`flex w-full min-w-0 shrink-0 overflow-hidden rounded-full border border-carbon/20 shadow-sm ${useMobileKeyLayout ? 'text-xs' : kbCompact ? 'text-sm' : 'text-base'}`}
                style={{ backgroundColor: 'rgba(255,255,255,0.92)' }}
              >
                <button
                  type="button"
                  onClick={switchToKeyboard}
                  title={t('checkout.keyboard', 'Keyboard')}
                  aria-label={t('checkout.keyboard', 'Keyboard')}
                  className={`flex min-w-0 flex-1 basis-0 items-center justify-center gap-1 px-2 font-semibold transition-colors sm:px-3 ${useMobileKeyLayout ? 'py-1.5' : kbCompact ? 'py-2' : 'py-2.5'} ${
                    inputMode !== 'keyboard' ? 'hover:bg-black/5' : ''
                  }`}
                  style={
                    inputMode === 'keyboard'
                      ? { backgroundColor: primaryColor, color: secondaryColor }
                      : { backgroundColor: 'transparent', color: '#333333' }
                  }
                >
                  <IoKeypad className="shrink-0 text-[1.1em]" aria-hidden />
                  <span className={kbAnyLandscape ? 'truncate' : 'hidden sm:inline'}>
                    {t('checkout.keyboard', 'Keyboard')}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={switchToQR}
                  title={t('checkout.scanQR', 'Scan QR')}
                  aria-label={t('checkout.scanQR', 'Scan QR')}
                  className={`flex min-w-0 flex-1 basis-0 items-center justify-center gap-1 px-2 font-semibold transition-colors sm:px-3 ${useMobileKeyLayout ? 'py-1.5' : kbCompact ? 'py-2' : 'py-2.5'} ${
                    inputMode !== 'qr' ? 'hover:bg-black/5' : ''
                  }`}
                  style={
                    inputMode === 'qr'
                      ? { backgroundColor: primaryColor, color: secondaryColor }
                      : { backgroundColor: 'transparent', color: '#333333' }
                  }
                >
                  <IoQrCode className="shrink-0 text-[1.1em]" aria-hidden />
                  <span className={kbAnyLandscape ? 'truncate' : 'hidden sm:inline'}>
                    {t('checkout.scanQR', 'Scan QR')}
                  </span>
                </button>
              </div>
            ) : null}
          </div>

          <div
            className={`flex w-full flex-col items-center ${useMobileKeyLayout ? 'gap-2' : kbCompact ? 'gap-3' : kbRegularLandscape ? 'gap-4' : 'gap-8'}`}
          >
            {error && inputMode === 'keyboard' && (
              <div
                className={`w-full rounded-lg border border-red-200 bg-red-50 p-3 text-center text-red-700 ${kbCompact ? 'text-sm' : 'text-base'}`}
                role="alert"
              >
                {error}
              </div>
            )}
            {inputMode === 'keyboard' && (
              <input
                type="text"
                value={internalInputValue}
                placeholder={placeholder}
                className={`w-full text-center ${
                  useMobileKeyLayout
                    ? kbViewportShortLandscape
                      ? 'border-2 p-2 text-sm placeholder:text-xs placeholder:text-carbon/55'
                      : 'border-2 p-2.5 text-base placeholder:text-sm placeholder:text-carbon/55'
                    : kbCompact
                      ? 'border-4 p-4 text-2xl'
                      : 'border-4 p-6 text-3xl'
                } ${
                  newspaperMode
                    ? 'border-newspaper-rule text-newspaper-black bg-newspaper-white'
                    : 'rounded-full text-carbon'
                }`}
                style={
                  newspaperMode
                    ? { borderRadius: '0' }
                    : { borderColor: primaryColor, backgroundColor: 'rgba(255,255,255,0.98)' }
                }
                onChange={(e) => {
                  const next = normalizeInput(e.target.value)
                  setInternalInputValue(next)
                  keyboardRef.current?.setInput(next)
                  onInputChange?.()
                }}
              />
            )}

            {inputMode === 'qr' && (
              <div
                className={`relative flex w-full items-center justify-center overflow-hidden rounded-2xl ring-2 ring-inset ${
                  kbViewportShortLandscape ? 'h-[120px]' : kbCompact ? 'h-[200px]' : 'h-[300px]'
                } ${newspaperMode ? 'bg-newspaper-white newspaper-border' : 'bg-black/40'}`}
                style={
                  newspaperMode
                    ? { borderRadius: '0' }
                    : { boxShadow: `inset 0 0 0 2px ${primaryColor}33` }
                }
                role="region"
                aria-label={t('checkout.scanQR', 'Scan QR')}
              >
                <div className="absolute inset-0 z-0">
                  <Preview cameraKey={cameraKey} capturePreview={unifiedCameraUtils.capturePreview} />
                </div>
                {!newspaperMode && (
                  <>
                    <div
                      className="pointer-events-none absolute inset-3 z-10 rounded-xl border-2 border-dashed opacity-80"
                      style={{ borderColor: `${primaryColor}aa` }}
                    />
                    <div
                      className="kb-scanline pointer-events-none absolute left-[12%] right-[12%] z-10 h-0.5 rounded-full opacity-80"
                      style={{ backgroundColor: primaryColor, boxShadow: `0 0 12px ${primaryColor}` }}
                    />
                    <div className="pointer-events-none absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-md backdrop-blur-sm">
                      <IoQrCode className="text-xl" style={{ color: primaryColor }} aria-hidden />
                    </div>
                    {isScanning && (
                      <div
                        className="pointer-events-none absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 shadow-md backdrop-blur-sm"
                        aria-live="polite"
                      >
                        <span
                          className="inline-block h-2 w-2 animate-pulse rounded-full"
                          style={{ backgroundColor: primaryColor }}
                        />
                      </div>
                    )}
                  </>
                )}
                {scanError && (
                  <div className="absolute bottom-2 left-2 right-2 z-30 rounded-lg bg-red-50 px-2 py-1 text-center text-xs text-red-700">
                    {scanError}
                  </div>
                )}
              </div>
            )}

            <div
              className={useMobileKeyLayout ? 'flex w-full gap-2' : kbCompact ? 'flex w-full gap-3' : 'flex w-auto gap-3'}
            >
              {newspaperMode ? (
                <>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="newspaper-border shrink-0 font-medium transition-all duration-200 hover:opacity-90"
                    style={{
                      backgroundColor: '#fafaf8',
                      color: '#1a1a1a',
                      border: '2px double #2a2a2a',
                      borderRadius: '0',
                      boxShadow: 'none',
                      padding: '16px 32px',
                      fontSize: '24px'
                    }}
                  >
                    {t('checkout.cancel', 'Cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={() => onEnter(normalizeInput(internalInputValue))}
                    disabled={!internalInputValue.trim() || isLoading}
                    className="newspaper-border flex shrink-0 items-center justify-center gap-2 font-medium transition-all duration-200 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    style={{
                      backgroundColor: '#1a1a1a',
                      color: '#fafaf8',
                      border: '2px double #2a2a2a',
                      borderRadius: '0',
                      boxShadow: 'none',
                      padding: '16px 32px',
                      fontSize: '24px'
                    }}
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                        {primarySubmitLoadingLabel}
                      </>
                    ) : (
                      primarySubmitLabel
                    )}
                  </button>
                </>
              ) : (
                <>
                  <Button
                    text={t('checkout.cancel', 'Cancel')}
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isLoading}
                    className={
                      useMobileKeyLayout
                        ? kbViewportShortLandscape
                          ? 'min-w-0 flex-1 !px-3 !py-1.5 !text-xs'
                          : 'min-w-0 flex-1 !px-4 !py-2 !text-sm'
                        : kbCompact
                          ? 'shrink-0 !px-6 !py-3 !text-xl'
                          : 'shrink-0 !px-12 !py-6 !text-3xl'
                    }
                  />
                  <Button
                    text={
                      isLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                          {primarySubmitLoadingLabel}
                        </span>
                      ) : (
                        primarySubmitLabel
                      )
                    }
                    onClick={() => onEnter(normalizeInput(internalInputValue))}
                    disabled={!internalInputValue.trim() || isLoading}
                    className={
                      useMobileKeyLayout
                        ? kbViewportShortLandscape
                          ? 'min-w-0 flex-1 !px-3 !py-1.5 !text-xs disabled:cursor-not-allowed disabled:opacity-50'
                          : 'min-w-0 flex-1 !px-4 !py-2 !text-sm disabled:cursor-not-allowed disabled:opacity-50'
                        : kbCompact
                          ? 'shrink-0 !px-6 !py-3 !text-xl disabled:cursor-not-allowed disabled:opacity-50'
                          : 'shrink-0 !px-12 !py-6 !text-3xl disabled:cursor-not-allowed disabled:opacity-50'
                    }
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Input area — sized relative to design canvas, not viewport */}
      {inputMode === 'keyboard' && (
        <div
          className={`kb-area w-full min-w-0 shrink-0 overflow-hidden bg-white/90 backdrop-blur-sm ${KB_WATERMARK_RESERVE_CLASS} ${kbViewportShortLandscape ? 'mt-auto' : ''} ${
            useMobileKeyLayout ? 'kb-portrait px-1 py-1' : kbCompact ? 'kb-compact p-4' : 'p-8'
          }`}
          style={kbAreaMaxStyle}
        >
          <div
            id="keyboard"
            className="simple-keyboard"
          />
        </div>
      )}

      {inputMode === 'qr' && (
        <div
          className={`flex w-full items-center justify-center bg-white/90 backdrop-blur-sm ${KB_WATERMARK_RESERVE_CLASS} ${kbViewportShortLandscape ? 'mt-auto' : ''} ${kbCompact ? 'py-2' : 'py-3'}`}
        >
          <div id="qr-scanner-element" ref={qrElementRef} className="hidden" aria-hidden />
        </div>
      )}

      <style>
        {`
          /* ── Base theme (desktop / large canvas) ──────────────── */
          .simple-keyboard.myTheme {
            background: transparent;
            border-radius: 0;
          }
          .simple-keyboard.myTheme .hg-button {
            height: 50px;
            display: flex;
            justify-content: center;
            align-items: center;
            background: rgba(255, 255, 255, 0.9);
            color: #333;
            border-radius: 8px;
            font-size: 20px;
            font-family: inherit;
          }
          .simple-keyboard.myTheme .hg-button:active {
            background: ${primaryColor};
            color: ${secondaryColor};
          }
          .simple-keyboard.myTheme .hg-button.hg-standardBtn {
            background: rgba(255, 255, 255, 0.9);
          }
          .simple-keyboard.myTheme .hg-button.hg-functionBtn {
            background: rgba(255, 255, 255, 0.7);
          }
          /* Large canvas, no compact/portrait wrapper — bigger keys */
          .kb-area:not(.kb-compact):not(.kb-portrait) .hg-button {
            height: 70px !important;
            font-size: 28px !important;
          }

          /* ── Compact (landscape mobile / small canvas) ───────── */
          .kb-compact .hg-button {
            height: 40px !important;
            font-size: 16px !important;
          }

          /* ── Portrait mobile — wrapper class the library can't touch ── */
          .kb-portrait .simple-keyboard {
            padding: 2px !important;
            width: 100% !important;
            max-width: 100% !important;
            overflow: visible !important;
            background: transparent !important;
          }
          .kb-portrait .hg-row {
            display: flex !important;
            flex-direction: row !important;
            align-items: stretch !important;
            width: 100% !important;
            margin-bottom: 2px !important;
            gap: 3px !important;
            min-height: 0 !important;
          }
          .kb-portrait .hg-row:not(:last-child) {
            margin-bottom: 2px !important;
          }
          .kb-portrait .hg-row .hg-button:not(:last-child),
          .kb-portrait .hg-row .hg-button-container:not(:last-child) {
            margin-right: 0 !important;
          }
          .kb-portrait .hg-button {
            flex: 1 1 0 !important;
            min-width: 0 !important;
            width: auto !important;
            max-width: none !important;
            height: ${keyHForCss}px !important;
            max-height: ${keyHForCss}px !important;
            min-height: ${keyHForCss}px !important;
            padding: 0 2px !important;
            margin: 0 !important;
            font-size: ${keyHForCss <= 24 ? 10 : 12}px !important;
            line-height: 1 !important;
            border-radius: 5px !important;
            box-sizing: border-box !important;
          }
          .kb-portrait .hg-button.hg-button-spaceWide {
            flex-grow: 4 !important;
            flex-basis: 0 !important;
          }
          @keyframes kbScanMove {
            0% { top: 18%; opacity: 0.5; }
            50% { opacity: 1; }
            100% { top: 78%; opacity: 0.5; }
          }
          .kb-scanline {
            animation: kbScanMove 2.2s ease-in-out infinite;
          }
        `}
      </style>
    </div>
  )
}

export default OnScreenKeyboard
